import React, { useState, useEffect, useMemo } from 'react';
import { styled } from 'styletron-react';
import { HeadingMedium } from 'baseui/typography';
import { toaster } from 'baseui/toast';
import { Avatar } from 'baseui/avatar';
import { S3Image } from '../components/S3Image';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Tag, KIND as TAG_KIND, HIERARCHY as TAG_HIERARCHY } from 'baseui/tag';
import { type MRT_ColumnDef } from 'mantine-react-table';

import { MantineDataTable } from '../components/MantineDataTable';
import { useUserEstate } from '../hooks/useUserEstate';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { AdminUsers } from './settings/AdminUsers';
import { useIsPhone } from '../hooks/useIsPhone';
import { useUpdateUserEstateStatus, useUserEstateStatuses } from '../hooks/useUserEstateStatus';
import { UsersPhoneView, type PhoneRequestItem, type PhoneUserItem } from './Users/UsersPhoneView';
import { initialsOf } from '../components/phone/PhoneCards';
import { ConfirmDialog } from '../components/ConfirmDialog';
import type { UserEstate } from '../api/userEstate';
import { getPropertyManagerUserTypeId } from '../api/propertyAdmin';
import { useT } from '../i18n/admin';
import { useFdCan } from '../hooks/useFdCan';
import { CreateResidentSheet } from '../components/CreateResidentSheet';
import { Notice } from '../components/ui/Notice';

// ── Layout shells ──────────────────────────────────────────────────────────
const Container = styled('div', {
  backgroundColor: '#fff',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const Header = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 24px 0 24px',
});

// ── Tabs — same style as Invitations ────────────────────────────────────────
const TabsRow = styled('div', {
  display: 'flex',
  borderBottom: '1px solid #E9ECEF',
  padding: '0 24px',
  marginTop: '4px',
});

const Tab = styled('button', (props: { $active: boolean }) => ({
  padding: '10px 0',
  marginRight: '24px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontWeight: props.$active ? '600' : '400',
  color: props.$active ? 'var(--tenant-primary, #111827)' : '#6B7280',
  borderBottom: props.$active ? '2px solid var(--tenant-primary, #111827)' : '2px solid transparent',
  fontSize: '14px',
  transition: 'all 0.2s ease',
  ':hover': { color: '#111827' },
}));

// ── Toolbar — identical to Invitations page ──────────────────────────────────
const ToolbarRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 24px',
});

// Pill-shaped search — identical to Invitations page
const SearchWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: '1',
  maxWidth: '280px',
  backgroundColor: '#EBEBEB',
  border: '1px solid #EBEBEB',
  borderRadius: '20px',
  padding: '7px 14px',
});

const ActiveSearchLabel = styled('span', {
  fontSize: '13px',
  color: '#6B7280',
  whiteSpace: 'nowrap',
});

const SearchInputEl = styled('input', {
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: '14px',
  color: '#374151',
  flex: 1,
  width: '100%',
  '::placeholder': {
    color: '#9CA3AF',
  },
});

// SAM-281: spinner shown in the search pill while a search request is in
// flight, so there's feedback right where the user typed — not just the
// table's own loading overlay.
const SearchSpinner = styled('div', {
  width: '14px',
  height: '14px',
  flexShrink: 0,
  boxSizing: 'border-box' as any,
  border: '2px solid #D1D5DB',
  borderTopColor: '#111827',
  borderRadius: '50%',
  animation: 'users-search-spin 0.6s linear infinite',
  '@keyframes users-search-spin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
});

// ── Table wrapper ─────────────────────────────────────────────────────────────
const Main = styled('div', {
  flex: 1,
  padding: '0 24px 24px 24px',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  boxSizing: 'border-box' as any,
});

// ── Status badge ──────────────────────────────────────────────────────────────
const StatusBadge = styled('div', (props: { $status: string }) => ({
  padding: '4px 8px',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: '500',
  display: 'inline-block',
  backgroundColor:
    props.$status === 'approved' ? '#E8F5E8' :
    props.$status === 'processing' ? '#FFF4E6' :
    props.$status === 'denied' ? '#FDE8E8' :
    props.$status === 'escalated' ? '#FEF2F2' :
    props.$status === 'waiting_for_customer' ? '#F0F9FF' :
    props.$status === 'flagged' ? '#FEF3C7' :
    props.$status === 'disabled' ? '#F3F4F6' :
    props.$status === 'delivered' ? '#E8F5E8' :
    props.$status === 'active' ? '#E8F5E8' :
    props.$status === 'suspended' ? '#FDE8E8' :
    props.$status === 'expired' ? '#F3F4F6' :
    props.$status === 'declined' ? '#FDE8E8' :
    props.$status === 'removed' ? '#F3F4F6' :
    props.$status === 'contact support' ? '#FEF3C7' :
    props.$status === 'unknown' ? '#F3F4F6' : '#F0F0F0',
  color:
    props.$status === 'approved' ? '#2D7738' :
    props.$status === 'processing' ? '#B7791F' :
    props.$status === 'denied' ? '#C53030' :
    props.$status === 'escalated' ? '#DC2626' :
    props.$status === 'waiting_for_customer' ? '#1D4ED8' :
    props.$status === 'flagged' ? '#D97706' :
    props.$status === 'disabled' ? '#6B7280' :
    props.$status === 'delivered' ? '#2D7738' :
    props.$status === 'active' ? '#2D7738' :
    props.$status === 'suspended' ? '#C53030' :
    props.$status === 'expired' ? '#6B7280' :
    props.$status === 'declined' ? '#C53030' :
    props.$status === 'removed' ? '#6B7280' :
    props.$status === 'contact support' ? '#D97706' :
    props.$status === 'unknown' ? '#6B7280' : '#666',
}));

type TabType = 'approved' | 'estate-requests' | 'all-others';

const TAB_VALUES: TabType[] = ['approved', 'estate-requests', 'all-others'];
const isTabType = (value: string | null): value is TabType =>
  value !== null && (TAB_VALUES as string[]).includes(value);

// URL query params are the source of truth for the search term and the active
// tab so both survive navigating into a user detail and back (or a refresh).
const SEARCH_PARAM = 'q';
const TAB_PARAM = 'tab';
// Cognito admin users (business_* groups) — its own view, not a user↔estate
// list, so it stays outside TabType and its search/count plumbing.
const ADMIN_TAB = 'admin-users';
// Users › Property Managers (tenant Admin): this same table filtered by the Property Manager user type, read-only.
// `?type=property_manager`; the type id is looked up by `user_type.kind`, never hard-coded.
const TYPE_PARAM = 'type';
const PM_VIEW = 'property_manager';
// One table row. The list endpoint returns one user↔estate relation per row,
// so a user linked to several estates shows up several times. Rows sharing
// the same `user.id` are grouped client-side (within the current server page)
// into a single parent row whose `subRows` are the individual relations.
// A user with a single estate has no `subRows` and renders as before.
type UserRow = UserEstate & {
  estateCount: number;
  subRows?: UserRow[];
};

const groupRowsByUser = (rows: UserEstate[]): UserRow[] => {
  const byUser = new Map<number, UserEstate[]>();
  rows.forEach((row) => {
    const id = row.user!.id;
    const bucket = byUser.get(id);
    if (bucket) bucket.push(row);
    else byUser.set(id, [row]);
  });
  return Array.from(byUser.values()).map((relations) => {
    if (relations.length === 1) return { ...relations[0], estateCount: 1 };
    return {
      ...relations[0],
      estateCount: relations.length,
      subRows: relations.map((r) => ({ ...r, estateCount: 1 })),
    };
  });
};

// Distinct non-empty values, in first-seen order.
const distinct = (values: Array<string | undefined | null>): string[] =>
  Array.from(new Set(values.filter((v): v is string => !!v)));

const isGroupRow = (row: UserRow) => (row.subRows?.length ?? 0) > 1;

const CountBadge = styled('span', {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '10px',
  fontSize: '12px',
  fontWeight: '600',
  color: '#374151',
  backgroundColor: '#E5E7EB',
  whiteSpace: 'nowrap',
});

// Small per-tab match count shown next to a tab label while a search is
// active, so the admin can tell at a glance which tabs have hits.
const TabCountBadge = styled('span', {
  display: 'inline-block',
  marginLeft: '6px',
  padding: '1px 7px',
  borderRadius: '10px',
  fontSize: '11px',
  fontWeight: '600',
  color: '#374151',
  backgroundColor: '#E5E7EB',
});

const TAB_LABELS: Record<TabType, string> = {
  approved: 'All',
  'estate-requests': 'Estate Requests',
  'all-others': 'All Others',
};

// Shown under the table's own "No matches found…" empty state when the
// active tab has zero hits but another tab has matches for the same search.
const CrossTabHint = styled('div', {
  padding: '10px 24px 0 24px',
  fontSize: '13px',
  color: '#6B7280',
});

const CrossTabLink = styled('button', {
  border: 'none',
  background: 'none',
  padding: 0,
  color: 'var(--tenant-primary, #111827)',
  fontWeight: '600',
  cursor: 'pointer',
  textDecoration: 'underline',
  fontSize: 'inherit',
});

const MutedText = styled('span', {
  color: '#9CA3AF',
  fontSize: '12px',
});

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

// `fixedTab`: the page shows that one view and no tab row (SAM-1307: Support > Estate Requests is this table).
export const Users = ({ fixedTab }: { fixedTab?: 'estate-requests' } = {}) => {
  const { fmt } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get(TAB_PARAM);
  const searchFromUrl = searchParams.get(SEARCH_PARAM) ?? '';
  const [activeTab, setActiveTab] = useState<TabType>(fixedTab ?? (isTabType(tabFromUrl) && tabFromUrl !== 'estate-requests' ? tabFromUrl : 'approved'));
  const { isAdmin } = useUserPermissions();
  // The tenant Front Desk adds residents (specs/front-desk-tenant-permisos.md); the server re-checks.
  const canAddResident = useFdCan('users', 'create', false);
  // SAM-1312 (U19): the estate group roles only read the requests (decision 3, 15 Oct); the server re-checks.
  const canDecideRequests = useFdCan('users', 'approve');
  const [addingResident, setAddingResident] = useState(false);
  const showAdminUsers = tabFromUrl === ADMIN_TAB && isAdmin && !fixedTab;
  const pmView = searchParams.get(TYPE_PARAM) === PM_VIEW && isAdmin;
  // undefined while it loads; null when the lookup failed (the list then stays empty rather than unfiltered).
  const [pmTypeId, setPmTypeId] = useState<number | null | undefined>(undefined);
  useEffect(() => {
    if (!pmView) return;
    let live = true;
    getPropertyManagerUserTypeId().then((id) => live && setPmTypeId(id)).catch(() => {
      if (!live) return;
      setPmTypeId(null);
      toaster.negative('Could not load Property Managers', { autoHideDuration: 3000 });
    });
    return () => { live = false; };
  }, [pmView]);
  const typeFilter = pmView && pmTypeId != null ? String(pmTypeId) : undefined;
  const typeReady = !pmView || pmTypeId != null;
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [searchInput, setSearchInput] = useState(searchFromUrl);
  const debouncedSearch = useDebounce(searchInput, 400);

  // Mirror the debounced search + tab into the URL. `replace: true` keeps the
  // history clean while typing, so "back" still leaves the page in one step.
  useEffect(() => {
    const term = debouncedSearch.trim();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (next.get(TAB_PARAM) === ADMIN_TAB && !fixedTab) return prev;
      if (term) next.set(SEARCH_PARAM, term); else next.delete(SEARCH_PARAM);
      if (fixedTab) next.delete(TAB_PARAM);
      else if (activeTab !== 'approved') next.set(TAB_PARAM, activeTab); else next.delete(TAB_PARAM);
      return next;
    }, { replace: true });
  }, [debouncedSearch, activeTab, fixedTab, setSearchParams]);

  // Keep local state in sync when the URL changes underneath us (browser
  // back/forward between two /users?q=... entries).
  useEffect(() => {
    if (searchFromUrl !== debouncedSearch.trim()) setSearchInput(searchFromUrl);
    if (!fixedTab) {
      if (isTabType(tabFromUrl) && tabFromUrl !== 'estate-requests' && tabFromUrl !== activeTab) setActiveTab(tabFromUrl);
      if ((tabFromUrl === null || tabFromUrl === 'estate-requests') && activeTab !== 'approved') setActiveTab('approved');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFromUrl, tabFromUrl]);

  const clearSearch = () => setSearchInput('');

  // Leaving the Admin Users view must rewrite the URL even when activeTab is
  // unchanged (the sync effect above only runs on a state change).
  const selectTab = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab !== 'approved') next.set(TAB_PARAM, tab); else next.delete(TAB_PARAM);
      return next;
    }, { replace: true });
  };
  const openAdminUsers = () => setSearchParams({ [TAB_PARAM]: ADMIN_TAB }, { replace: true });

  // SAM-214/SAM-281: route the single search box by input shape so a phone
  // number is searched as a phone, not misread as a user ID.
  //   - Phone: starts with "+" or is a long digit string (>= 7 significant
  //     digits once separators are stripped). Sent through `user_search`,
  //     normalized (spaces/hyphens/parens/'+' stripped) per SAM-281.
  //   - User ID: a short purely-numeric string (<= 6 digits) that is not a phone.
  //   - Email: contains "@". Sent through `user_search` only — an email can
  //     never match an estate name, so sending it as `estate_search` too is
  //     just a wasted OR clause on the backend.
  //   - Anything else: name/last name/full name + estate text search, sent to
  //     both `user_search` and `estate_search` so the backend ORs across all
  //     of them in one round trip (see functions/crud/lambda_function.py,
  //     the `route == 'user_estate'` block in process_get_all_query /
  //     get_total_records_count).
  //
  // The backend is being extended (in parallel) to also match `user.tel`
  // (digits-only compare) and full name within `user_search` — this param
  // contract (`user_search` / `estate_search` / `user_id`) does not change,
  // only what the backend matches against it.
  const rawSearch = debouncedSearch.trim();
  const phoneDigits = rawSearch.replace(/[\s\-().+]/g, '');
  const isPhoneSearch =
    rawSearch.length > 0 &&
    /^\+?[\d\s\-().]+$/.test(rawSearch) &&
    (rawSearch.startsWith('+') || phoneDigits.length >= 7);
  const isUserIdSearch = !isPhoneSearch && /^\d{1,6}$/.test(rawSearch);
  const isEmailSearch = !isPhoneSearch && !isUserIdSearch && rawSearch.includes('@');

  // Phone → normalized digits through user_search; email → user_search only;
  // name/text → user_search + estate_search
  const userSearch = isUserIdSearch
    ? undefined
    : isPhoneSearch
      ? phoneDigits || undefined
      : rawSearch || undefined;
  const estateSearch =
    !isUserIdSearch && !isPhoneSearch && !isEmailSearch ? (rawSearch || undefined) : undefined;
  const userIdFilter = isUserIdSearch ? rawSearch : undefined;

  const {
    userEstates,
    isLoading: isLoadingEstates,
    error: errorEstates,
    refetch: refetchEstates,
    totalElements: totalEstates,
  } = useUserEstate({
    page, limit,
    status_id: '1',
    user_search: userSearch,
    estate_search: estateSearch,
    user_id: userIdFilter,
    user_type_id_in: typeFilter,
    // SAM-433: while a search term is active, run all three tabs' queries so
    // each tab's match count can be shown, even for tabs the admin isn't
    // currently viewing. Without a search, only the active tab fetches.
    enabled: !!fixedTab && typeReady,
  });

  const {
    userEstates: approvedUserEstates,
    isLoading: isLoadingApproved,
    error: errorApproved,
    refetch: refetchApproved,
    totalElements: totalApproved,
  } = useUserEstate({
    page, limit,
    status_id: 2,
    user_search: userSearch,
    estate_search: estateSearch,
    user_id: userIdFilter,
    user_type_id_in: typeFilter,
    enabled: !fixedTab && !showAdminUsers && typeReady && (activeTab === 'approved' || !!rawSearch),
  });

  const {
    userEstates: allOthersUserEstates,
    isLoading: isLoadingAllOthers,
    error: errorAllOthers,
    refetch: refetchAllOthers,
    totalElements: totalAllOthers,
  } = useUserEstate({
    page, limit,
    status_id_in: '3,4,5,6,7,8',
    user_search: userSearch,
    estate_search: estateSearch,
    user_id: userIdFilter,
    user_type_id_in: typeFilter,
    enabled: !fixedTab && !showAdminUsers && typeReady && (activeTab === 'all-others' || !!rawSearch),
  });

  useEffect(() => { setPage(1); }, [activeTab]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  useEffect(() => {
    if (errorEstates) toaster.negative('Error loading estate requests', { autoHideDuration: 3000 });
  }, [errorEstates]);
  useEffect(() => {
    if (errorApproved) toaster.negative('Error loading approved users', { autoHideDuration: 3000 });
  }, [errorApproved]);
  useEffect(() => {
    if (errorAllOthers) toaster.negative('Error loading other users', { autoHideDuration: 3000 });
  }, [errorAllOthers]);

  const currentTabData = (() => {
    switch (activeTab) {
      case 'estate-requests':
        return { data: userEstates || [], isLoading: isLoadingEstates, error: errorEstates, refetch: refetchEstates, totalElements: totalEstates || 0 };
      case 'approved':
        return { data: approvedUserEstates || [], isLoading: isLoadingApproved, error: errorApproved, refetch: refetchApproved, totalElements: totalApproved || 0 };
      case 'all-others':
        return { data: allOthersUserEstates || [], isLoading: isLoadingAllOthers, error: errorAllOthers, refetch: refetchAllOthers, totalElements: totalAllOthers || 0 };
      default:
        return { data: [], isLoading: false, error: null, refetch: undefined, totalElements: 0 };
    }
  })();
  // SAM-801: a list that failed to load (403 for a Front Desk, say) is not an empty one.
  const loadFailed = !!currentTabData.error && !currentTabData.isLoading && currentTabData.data.length === 0;

  // SAM-433: when the active tab has no matches for the current search but
  // another tab does, surface it instead of leaving a bare "No matches
  // found" — the admin's user may simply be pending or on a different status.
  const crossTabMatches = useMemo(() => {
    if (!rawSearch) return [];
    const counts: Record<TabType, number> = {
      approved: totalApproved || 0,
      'estate-requests': totalEstates || 0,
      'all-others': totalAllOthers || 0,
    };
    return TAB_VALUES.filter((t) => t !== 'estate-requests' && t !== activeTab && counts[t] > 0).map((t) => ({
      tab: t,
      count: counts[t],
    }));
  }, [rawSearch, activeTab, totalApproved, totalEstates, totalAllOthers]);

  // SAM-219: Hide orphan user↔estate rows whose user was deleted or does not
  // resolve. They cannot be opened (navigation needs the user id) and should
  // not be presented as actionable users. The underlying data-integrity issue
  // is tracked for the backend; this guards the list in the meantime.
  const orphanRowCount = currentTabData.data.filter((r) => !r?.user?.id).length;
  const visibleRows = useMemo(() => currentTabData.data.filter((r) => !!r?.user?.id), [currentTabData.data]);
  const groupedRows = useMemo(() => groupRowsByUser(visibleRows), [visibleRows]);

  const columns = useMemo<MRT_ColumnDef<UserRow>[]>(
    () => [
      // SAM-1312 (U22): no ID column (internal) and no Estate Status (always «--»): 8 columns did not fit at 1440.
      {
        accessorKey: 'user.name',
        header: 'User',
        size: 250,
        Cell: ({ row }) => {
          if (row.depth > 0) return null;
          const u = row.original;
          const name = u?.user?.name && u?.user?.last_name
            ? `${u.user.name} ${u.user.last_name}`.trim()
            : u?.user?.name || '--';
          return name !== '--' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {(u.user as any)?.profile_picture ? (
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  flexShrink: 0,
                  backgroundColor: '#F3F4F6',
                  border: '1px solid #E5E7EB',
                }}>
                  <S3Image
                    imageKey={(u.user as any).profile_picture}
                    alt={name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    showLoading={true}
                  />
                </div>
              ) : (
                  <Avatar
                    name={name}
                    size="24px"
                    overrides={{
                      Root: { style: { fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } },
                      Initials: { style: { fontSize: '10px' } },
                    }}
                  />
              )}
              <span>{name}</span>
            </div>
          ) : <span>--</span>;
        },
      },
      {
        accessorKey: 'estate.name',
        header: 'Estate',
        size: 150,
        Cell: ({ row }) => {
          if (isGroupRow(row.original)) {
            return <CountBadge>{row.original.estateCount} estates</CountBadge>;
          }
          return row.original?.estate?.name || '--';
        },
      },
      {
        accessorKey: 'estate_group.name',
        header: 'Estate Group',
        size: 180,
        Cell: ({ row }) => {
          if (isGroupRow(row.original)) {
            const groups = distinct(row.original.subRows!.map((r) => r.estate_group?.name));
            if (groups.length === 0) return '--';
            if (groups.length === 1) return groups[0];
            return <MutedText>{groups.length} groups</MutedText>;
          }
          return row.original?.estate_group?.name || '--';
        },
      },
      {
        // SAM-218: The role (Owner / Companion / …) is a property of the
        // user↔estate relation, not the user — the same person can be Owner in
        // one property and Companion in another. Read it from `user_type.name`,
        // which the list query already expands.
        accessorKey: 'user_type.name',
        header: 'User Type',
        size: 130,
        Cell: ({ row }) => {
          if (isGroupRow(row.original)) {
            const types = distinct(row.original.subRows!.map((r) => r.user_type?.name));
            if (types.length === 0) return '--';
            if (types.length === 1) return types[0];
            return <MutedText>Mixed ({types.join(', ')})</MutedText>;
          }
          return row.original?.user_type?.name || '--';
        },
      },
      {
        accessorKey: 'user_estate_status.name',
        header: 'Request Status',
        size: 140,
        Cell: ({ row }) => {
          if (isGroupRow(row.original)) {
            const statuses = distinct(row.original.subRows!.map((r) => r.user_estate_status?.name));
            if (statuses.length > 1) {
              return (
                <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <MutedText>Mixed</MutedText>
                </div>
              );
            }
          }
          const statusName = row.original?.user_estate_status?.name || 'Unknown';
          return (
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <StatusBadge $status={statusName.toLowerCase()}>{statusName}</StatusBadge>
            </div>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        size: 120,
        Cell: ({ row }) => {
          if (isGroupRow(row.original) || !row.original?.created_at) return '--';
          return fmt.date(row.original.created_at);
        },
      },
    ],
    [navigate, fmt]
  );

  // SAM-944 (mocks M1 and M3): on a phone the list is cards, the tabs are chips and a request has Approve / Reject.
  const isPhone = useIsPhone();
  const { statuses: userEstateStatuses } = useUserEstateStatuses();
  const updateEstateStatus = useUpdateUserEstateStatus();
  const [decidingKey, setDecidingKey] = useState<string | null>(null);
  // Reject asks first, in the admin's own confirmation dialog (not window.confirm).
  const [rejecting, setRejecting] = useState<PhoneRequestItem | null>(null);
  // The statuses are found by NAME in the tenant's own list, never by number. Dev (QA, 15 Oct): Processing, Active,
  // Expired, Suspended, Declined, Removed, Contact Support.
  const statusIdByName = (pattern: RegExp): number | undefined =>
    userEstateStatuses.find((st) => pattern.test(st.name ?? ''))?.id;
  const approvedStatusId = statusIdByName(/^(approved|active)$/i);
  // Reject is the status for a declined request («Declined» in dev; the backend's older name is Denied), and never
  // «Removed» (that is dropping an approved resident) nor «Suspended» / «Expired».
  const rejectedStatusId = statusIdByName(/^(rejected|denied|declined)$/i);

  // SAM-1308: Admin Users on a phone is a card list too; the Users tabs stay as its chips.
  if (isPhone && showAdminUsers) {
    return (
      <AdminUsers
        embedded
        phoneTabs={{
          title: 'Users',
          chips: [
            { id: 'approved', label: TAB_LABELS.approved, on: false },
            { id: 'all-others', label: TAB_LABELS['all-others'], on: false },
            { id: ADMIN_TAB, label: 'Admin Users', on: true },
          ],
          onPick: (id) => (id === ADMIN_TAB ? undefined : selectTab(id as TabType)),
        }}
      />
    );
  }

  if (isPhone && !showAdminUsers) {
    const requestsMode = activeTab === 'estate-requests';
    const dayMs = 24 * 60 * 60 * 1000;
    const sentLabel = (iso?: string): { sent: string; old: boolean } => {
      if (!iso) return { sent: '', old: false };
      const days = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / dayMs));
      const rel = days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
      return { sent: `Sent ${fmt.date(iso)} · ${rel}`, old: days >= 3 };
    };
    const nameOf = (r: UserEstate) => [r.user?.name, r.user?.last_name].filter(Boolean).join(' ').trim() || '--';
    const toneOf = (label: string): 'ok' | 'muted' | 'warn' =>
      /approved|active/i.test(label) ? 'ok' : /pending|processing|request/i.test(label) ? 'warn' : 'muted';
    const phoneUsers: PhoneUserItem[] = groupedRows.map((r) => {
      const group = isGroupRow(r);
      const types = distinct((group ? r.subRows! : [r]).map((x) => x.user_type?.name));
      const sub = group
        ? `${types.join(', ') || '--'} · ${r.estateCount} estates`
        : [r.user_type?.name, r.estate?.name].filter(Boolean).join(' · ') || '--';
      const label = group && distinct(r.subRows!.map((x) => x.user_estate_status?.name)).length > 1
        ? 'Mixed'
        : r.user_estate_status?.name || 'Unknown';
      return { key: `u${r.user!.id}`, userId: r.user!.id, name: nameOf(r), initials: initialsOf(nameOf(r)), sub, statusLabel: label, statusTone: toneOf(label) };
    });
    const phoneRequests: PhoneRequestItem[] = visibleRows.map((r) => ({
      key: `${r.user_id}-${r.estate_id}`,
      userId: r.user!.id,
      estateId: r.estate_id,
      name: nameOf(r),
      initials: initialsOf(nameOf(r)),
      wants: [r.user_type?.name, r.estate?.name].filter(Boolean).join(' · ') || '--',
      ...sentLabel(r.created_at),
    }));
    const applyDecision = (item: PhoneRequestItem, decision: 'approve' | 'reject') => {
      const statusId = decision === 'approve' ? approvedStatusId : rejectedStatusId;
      if (statusId === undefined) return;
      setDecidingKey(item.key);
      updateEstateStatus.mutate(
        { user_id: item.userId, estate_id: item.estateId, status_id: statusId },
        { onSettled: () => setDecidingKey(null) },
      );
    };
    const decide = (item: PhoneRequestItem, decision: 'approve' | 'reject') => {
      if (decision === 'reject') setRejecting(item);
      else applyDecision(item, 'approve');
    };
    const total = Math.max(0, currentTabData.totalElements - orphanRowCount);
    return (
      <>
      <UsersPhoneView
        title={requestsMode ? 'Estate Requests' : pmView ? 'Property Managers' : 'Users'}
        summary={requestsMode ? `${total.toLocaleString('en-US')} waiting for approval` : `${total.toLocaleString('en-US')} records`}
        searchValue={searchInput}
        onSearch={setSearchInput}
        chips={fixedTab ? [] : [
          { id: 'approved', label: TAB_LABELS.approved },
          { id: 'all-others', label: TAB_LABELS['all-others'] },
          ...(isAdmin ? [{ id: ADMIN_TAB, label: 'Admin Users' }] : []),
        ]}
        activeChip={activeTab}
        onChip={(id) => (id === ADMIN_TAB ? openAdminUsers() : selectTab(id as TabType))}
        mode={requestsMode ? 'requests' : 'users'}
        users={phoneUsers}
        requests={phoneRequests}
        loading={currentTabData.isLoading}
        failed={!!currentTabData.error && !currentTabData.isLoading && currentTabData.data.length === 0}
        onRetry={() => currentTabData.refetch?.()}
        page={page}
        limit={limit}
        total={total}
        onPage={setPage}
        onOpen={(userId) => navigate(`/users/${userId}`, { state: { from: `${location.pathname}${location.search}` } })}
        onDecide={requestsMode && canDecideRequests && approvedStatusId !== undefined ? decide : undefined}
        canReject={rejectedStatusId !== undefined}
        busyKey={decidingKey}
        emptyText={rawSearch ? `No matches found for "${rawSearch}"` : requestsMode ? 'No requests waiting.' : 'No users to show.'}
      />
      <ConfirmDialog
        isOpen={!!rejecting}
        onClose={() => setRejecting(null)}
        onConfirm={() => {
          if (rejecting) applyDecision(rejecting, 'reject');
          setRejecting(null);
        }}
        title="Reject request"
        message={rejecting ? `${rejecting.name}'s request for ${rejecting.wants} will be rejected.` : ''}
        confirmText="Reject"
      />
      </>
    );
  }

  return (
    <Container>
      {/* Title */}
      <Header>
        <HeadingMedium style={{ margin: 0 }}>{fixedTab ? 'Estate Requests' : pmView ? 'Property Managers' : 'Users'}</HeadingMedium>
      </Header>

      {/* Tabs — same style as Invitations */}
      {!fixedTab && (
      <TabsRow>
        <Tab $active={!showAdminUsers && activeTab === 'approved'} onClick={() => selectTab('approved')}>
          All
          {rawSearch && !showAdminUsers && <TabCountBadge>{totalApproved || 0}</TabCountBadge>}
        </Tab>
        <Tab $active={!showAdminUsers && activeTab === 'all-others'} onClick={() => selectTab('all-others')}>
          All Others
          {rawSearch && !showAdminUsers && <TabCountBadge>{totalAllOthers || 0}</TabCountBadge>}
        </Tab>
        {isAdmin && (
          <Tab $active={showAdminUsers} onClick={openAdminUsers}>
            Admin Users
          </Tab>
        )}
      </TabsRow>
      )}

      {showAdminUsers ? (
        <AdminUsers embedded />
      ) : (
      <>

      {/* Toolbar — pill search, identical to Invitations page */}
      <ToolbarRow>
        <SearchWrapper>
          {/* SAM-281: while a search is in flight, swap the magnifier for a
              spinner so there's visible feedback that the server is searching. */}
          {rawSearch && currentTabData.isLoading ? (
            <SearchSpinner role="status" aria-label="Searching" />
          ) : (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2" style={{ flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          )}
          <SearchInputEl
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, email, phone, estate, or user ID..."
          />
          {searchInput && (
            <button
              onClick={clearSearch}
              aria-label="Clear search"

              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#6B7280' }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </SearchWrapper>
        {rawSearch && (
          <>
            <ActiveSearchLabel>Showing results for</ActiveSearchLabel>
            <Tag
              kind={TAG_KIND.neutral}
              hierarchy={TAG_HIERARCHY.primary}
              closeable
              onActionClick={clearSearch}
              title={rawSearch}
              overrides={{
                Root: {
                  props: { 'data-testid': 'users-active-search-tag' },
                  style: {
                    marginTop: 0,
                    marginBottom: 0,
                    marginLeft: 0,
                    marginRight: 0,
                    backgroundColor: '#111827',
                    borderTopLeftRadius: '20px',
                    borderTopRightRadius: '20px',
                    borderBottomLeftRadius: '20px',
                    borderBottomRightRadius: '20px',
                  },
                },
                Text: { style: { fontSize: '13px', maxWidth: '240px' } },
                Action: { props: { 'aria-label': 'Clear search' } },
              }}
            >
              {rawSearch}
            </Tag>
          </>
        )}
        {canAddResident && !pmView && (
          <button type="button" className="btn primary" style={{ marginLeft: 'auto' }} onClick={() => setAddingResident(true)}>
            Add resident
          </button>
        )}
      </ToolbarRow>

      {rawSearch && !currentTabData.isLoading && currentTabData.totalElements === 0 && crossTabMatches.length > 0 && (
        <CrossTabHint>
          No matches in {TAB_LABELS[activeTab]}, but{' '}
          {crossTabMatches.map((m, i) => (
            <React.Fragment key={m.tab}>
              {i > 0 && ' · '}
              <CrossTabLink onClick={() => setActiveTab(m.tab)}>
                {m.count} in {TAB_LABELS[m.tab]}
              </CrossTabLink>
            </React.Fragment>
          ))}
        </CrossTabHint>
      )}

      {/* Table */}
      <Main>
        {loadFailed ? (
          <Notice variant="error" style={{ margin: '24px' }}>
            Could not load the users.{' '}
            <button type="button" className="btn" onClick={() => currentTabData.refetch?.()}>
              Try again
            </button>
          </Notice>
        ) : (
          <MantineDataTable
            columns={columns}
            data={groupedRows}
            loading={currentTabData.isLoading}
            // SAM-1323 (U22): Created is off by default so the table fits at 1440 with the menu open; the column menu brings it back
            initialState={{ columnVisibility: { created_at: false } }}
            // SAM-281: name the active search term in the empty state instead
            // of the generic "no records" default.
            emptyStateMessage={rawSearch ? `No matches found for "${rawSearch}"` : undefined}
            enableGlobalFilter={false}
            enableExpanding
            getSubRows={(row) => row.subRows}
            manualPagination
            pageCount={Math.ceil(currentTabData.totalElements / limit)}
            state={{
              pagination: {
                pageIndex: page - 1,
                pageSize: limit,
              },
            }}
            onPaginationChange={(updater: any) => {
              const newState =
                typeof updater === 'function'
                  ? updater({ pageIndex: page - 1, pageSize: limit })
                  : updater;
              setPage(newState.pageIndex + 1);
              setLimit(newState.pageSize);
            }}
            totalRecords={Math.max(0, currentTabData.totalElements - orphanRowCount)}
            onRowClick={(row) => {
              if (row.user?.id) {
                // SAM-215: open the detail on Basic Information (the component's
                // default) instead of forcing the Estate tab.
                // Pass the list URL (search + tab) so the detail's back button
                // returns to the same filtered view.
                navigate(`/users/${row.user.id}`, {
                  state: { from: `${location.pathname}${location.search}` },
                });
              }
            }}
          />
        )}
      </Main>
      </>
      )}
      <CreateResidentSheet isOpen={addingResident} onClose={() => setAddingResident(false)} />
    </Container>
  );
};
